import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Palette,
  Type,
  Heading1,
  Heading2,
  Heading3,
  Link,
  Quote,
  Code
} from 'lucide-react';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = '',
  className = '',
  style = {}
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSizePicker, setShowSizePicker] = useState(false);

  const colors = [
    // Basic colors
    '#000000', '#374151', '#6B7280', '#FFFFFF',
    // Red tones  
    '#EF4444', '#FCA5A5', '#7F1D1D', '#FEF2F2',
    // Orange/Yellow tones
    '#F59E0B', '#FCD34D', '#92400E', '#FFFBEB',
    // Green tones
    '#10B981', '#6EE7B7', '#047857', '#ECFDF5',
    // Blue tones
    '#3B82F6', '#93C5FD', '#1E40AF', '#EFF6FF',
    // Purple tones
    '#8B5CF6', '#C4B5FD', '#5B21B6', '#F5F3FF',
    // Pink tones
    '#EC4899', '#F9A8D4', '#BE185D', '#FDF2F8'
  ];

  const sizes = [
    { label: 'Small', value: '12px' },
    { label: 'Normal', value: '14px' },
    { label: 'Large', value: '18px' },
    { label: 'Extra Large', value: '24px' }
  ];

  const executeCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  }, [onChange]);

  const handleFormat = useCallback((command: string, value?: string) => {
    editorRef.current?.focus();
    executeCommand(command, value);
  }, [executeCommand]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  }, [onChange]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  const handleColorChange = useCallback((color: string) => {
    handleFormat('foreColor', color);
    setShowColorPicker(false);
  }, [handleFormat]);

  const handleSizeChange = useCallback((size: string) => {
    handleFormat('fontSize', '3');
    // Apply custom font size via CSS
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (!range.collapsed) {
        const span = document.createElement('span');
        span.style.fontSize = size;
        try {
          range.surroundContents(span);
        } catch (e) {
          span.appendChild(range.extractContents());
          range.insertNode(span);
        }
      }
    }
    setShowSizePicker(false);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [handleFormat, onChange]);

  const createLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) {
      handleFormat('createLink', url);
    }
  }, [handleFormat]);

  // Initialize editor content on mount
  useEffect(() => {
    if (editorRef.current && value) {
      editorRef.current.innerHTML = value;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update editor content when value changes externally
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      // Only update if editor is not focused (to avoid interfering with typing)
      if (document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  return (
    <div className={`rich-text-editor border border-gray-300 rounded-md bg-white ${className}`} style={style}>
      {/* Toolbar */}
      <div className="rich-text-editor-toolbar flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
        {/* Text Formatting */}
        <button
          type="button"
          onClick={() => handleFormat('bold')}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat('italic')}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat('underline')}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Underline"
        >
          <Underline className="w-4 h-4" />
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Headers */}
        <button
          type="button"
          onClick={() => handleFormat('formatBlock', 'h1')}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Header 1"
        >
          <Heading1 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat('formatBlock', 'h2')}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Header 2"
        >
          <Heading2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat('formatBlock', 'h3')}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Header 3"
        >
          <Heading3 className="w-4 h-4" />
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Lists */}
        <button
          type="button"
          onClick={() => handleFormat('insertUnorderedList')}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat('insertOrderedList')}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Color Picker */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Text Color"
          >
            <Palette className="w-4 h-4" />
          </button>
          {showColorPicker && (
            <div className="color-picker-dropdown absolute top-full left-0 mt-1 p-3 bg-white border border-gray-300 rounded shadow-lg z-10 min-w-48">
              <div className="space-y-2">
                <div className="text-xs text-gray-600 font-medium mb-2">Text Color</div>
                <div className="grid grid-cols-4 gap-2">
                  {colors.map((color, index) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => handleColorChange(color)}
                      className="w-8 h-8 rounded border-2 border-gray-300 hover:scale-110 transition-transform hover:border-blue-500"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
                <button
                  onClick={() => setShowColorPicker(false)}
                  className="w-full mt-2 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Font Size */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowSizePicker(!showSizePicker)}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Font Size"
          >
            <Type className="w-4 h-4" />
          </button>
          {showSizePicker && (
            <div className="size-picker-dropdown absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 min-w-24">
              {sizes.map((size) => (
                <button
                  key={size.value}
                  type="button"
                  onClick={() => handleSizeChange(size.value)}
                  className="block w-full px-3 py-2 text-left hover:bg-gray-100 transition-colors text-sm"
                  style={{ fontSize: size.value }}
                >
                  {size.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Additional Features */}
        <button
          type="button"
          onClick={createLink}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Insert Link"
        >
          <Link className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat('formatBlock', 'blockquote')}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Quote"
        >
          <Quote className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat('formatBlock', 'pre')}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Code Block"
        >
          <Code className="w-4 h-4" />
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        className="p-3 min-h-[120px] focus:outline-none"
        style={{ 
          lineHeight: '1.5',
          textAlign: 'left',
          direction: 'ltr',
          ...style
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />


    </div>
  );
};

export default RichTextEditor;
